<template>
  <div>
    <div id="title-panel">
      <div id="title-container">
        <h2>{{ t('projectsCoworkers.coworkers') }}</h2>
        <div id="coworkers-counter">{{ coworkers?.length ?? 0 }}</div>
      </div>

      <p id="description">
        {{ t('projectsCoworkers.description') }}
      </p>

      <vf-button
        id="add-coworker-button"
        :size="ButtonSize.LARGE"
        :variant="ButtonVariant.SECONDARY"
        @click="openAddCoworkerModal"
      >
        {{ t('projectsCoworkers.addCoworker') }}
      </vf-button>
    </div>

    <table-wrapper
      v-model:page-options="pageOptions"
      v-model:filters="filters"
      action-icon="delete"
      :fields="tableFields"
      :filter-configuration="filterConfiguration"
      :items="coworkers"
      :loading="isLoading"
      :mode="TableMode.CLIENT_SIDE"
      @action-icon-click="handleRemoveCoworkerIconClick"
    ></table-wrapper>

    <select-modal
      ref="modalRef"
      :modal-title="t('projectsCoworkers.addCoworker')"
      modal-id="add-coworker-modal"
      :ok-button-label="t('generic.add')"
      :endpoint="getSelectModalEndpoint()"
      track-by="lastName"
      :label="nameAndEmailLabel"
      :ok-button-busy="isAddingCoworker"
      :prevent-closing-on-ok="true"
      @option-selected="handleAddCoworker"
    />
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '../../composables/useToast';
  import axios, { AxiosResponse } from 'axios';
  import endpointsList from '../../helpers/endpointsList';
  import TableWrapper from '../TableWrapper/TableWrapper.vue';
  import SelectModal from '../SelectModal/SelectModal.vue';
  import {
    TableFieldsBuilder,
    TableFiltersBuilder,
    TableMode,
    useTableWrapper,
  } from '../TableWrapper';
  import {
    ICoworker,
    ICoworkerWithSupplier,
    IDigitalizationCoworkerDto,
    Serialized,
    IAddCoworkerToDigitalizationProjectDto,
    ProjectType,
    UserGroup,
    AdGroup,
  } from 'types';
  import { ButtonSize, ButtonVariant } from '../VfButton/VfButton.types';

  const { t } = useI18n();
  const { createSuccessToast, createErrorToast } = useToast();

  const props = defineProps({
    mode: {
      type: String as () => ProjectType,
      required: true,
    },
    projectId: {
      type: String,
      required: true,
    },
    supplierId: {
      type: String,
      required: false,
    },
  });

  const coworkers = ref<ICoworker[]>([]);
  const isAddingCoworker = ref(false);
  const TABLE_NAMESPACE = 'pc';
  const { pageOptions, filters, isLoading } = useTableWrapper<ICoworker>(
    TableMode.CLIENT_SIDE,
    'firstName',
    getProjectsCoworkers,
    undefined,
    true,
    TABLE_NAMESPACE,
  );
  const filterConfiguration = new TableFiltersBuilder<ICoworker>({
    translationKey: 'projectsCoworkers',
  })
    .addInputFilter('firstName', { width: '208px' })
    .addInputFilter('lastName', { width: '208px' })
    .addInputFilter('email', { width: '208px' })
    .build();
  const tableFields = computed(() =>
    new TableFieldsBuilder<ICoworker>(
      {
        sortable: true,
        labelTranslationKey: 'generic',
      },
      { actionIcon: true },
    )
      .addField('firstName', { width: '30%' })
      .addField('lastName', { width: '35%' })
      .addField('email', { width: '35%' })
      .build(),
  );

  function getSelectModalEndpoint() {
    if (props.mode === ProjectType.SUPPLIER_PROJECT && props.supplierId) {
      return endpointsList.suppliers.getCoworkersForSupplier(props.supplierId);
    } else if (props.mode === ProjectType.DIGITALIZATION_PROJECT) {
      return endpointsList.adIntegration.getAllUsersFromDigitalizationProjectAdGroup(
        AdGroup.DIGITALIZATION_COWORKERS,
      );
    }
  }

  async function getProjectsCoworkers() {
    if (!props.supplierId) return;
    isLoading.value = true;
    try {
      const response: AxiosResponse<ICoworkerWithSupplier[]> = await axios.get(
        endpointsList.projects.getCoworkersForProjectForSupplier(
          props.projectId,
          props.supplierId,
        ),
      );
      coworkers.value = response.data;
    } catch {
      createErrorToast(t('projectsCoworkers.errors.couldNotLoadProjects'));
    } finally {
      isLoading.value = false;
    }
  }

  async function getDigitalizationProjectsCoworkers() {
    isLoading.value = true;
    try {
      const response: AxiosResponse<IDigitalizationCoworkerDto[]> =
        await axios.get(
          endpointsList.digitalizationProjects.getDigitalizationProjectCoworkers(
            props.projectId,
          ),
        );
      coworkers.value = response.data;
    } catch {
      createErrorToast(t('projectsCoworkers.errors.couldNotLoadProjects'));
    } finally {
      isLoading.value = false;
    }
  }

  async function handleRemoveCoworkerIconClick(coworker: ICoworker) {
    try {
      isLoading.value = true;
      if (props.mode === ProjectType.SUPPLIER_PROJECT) {
        await removeCoworker(coworker);
      } else if (props.mode === ProjectType.DIGITALIZATION_PROJECT) {
        await removeDigitalizationCoworker(coworker);
      }
      createSuccessToast(t('projectsCoworkers.coworkerRemoved'));
    } catch {
      createErrorToast(t('projectsCoworkers.errors.couldNotRemoveCoworker'));
    } finally {
      isLoading.value = false;
      if (props.mode === ProjectType.SUPPLIER_PROJECT) {
        getProjectsCoworkers();
      } else if (props.mode === ProjectType.DIGITALIZATION_PROJECT) {
        getDigitalizationProjectsCoworkers();
      }
    }
  }

  async function removeCoworker(coworker: ICoworker) {
    await axios.delete(
      endpointsList.projects.removeCoworkerFromProject(
        props.projectId,
        coworker.oid,
      ),
    );
  }

  async function removeDigitalizationCoworker(coworker: ICoworker) {
    await axios.delete(
      endpointsList.digitalizationProjects.removeCoworkerFromDigitalizationProject(
        props.projectId,
        coworker.oid,
      ),
    );
  }

  async function handleAddCoworker(coworker: ICoworker) {
    isAddingCoworker.value = true;
    const { oid } = coworker;
    try {
      if (props.mode === ProjectType.SUPPLIER_PROJECT) {
        await axios.patch(
          endpointsList.projects.addCoworkerToProject(props.projectId, oid),
        );
      } else if (props.mode === ProjectType.DIGITALIZATION_PROJECT) {
        const body: Serialized<IAddCoworkerToDigitalizationProjectDto> = {
          digitalizationProjectId: props.projectId,
          digitalizationCoworkerOid: oid,
        };
        await axios.post(
          endpointsList.digitalizationProjects
            .addCoworkerToDigitalizationProject,
          body,
        );
      }
      if (props.mode === ProjectType.SUPPLIER_PROJECT) {
        getProjectsCoworkers();
      } else if (props.mode === ProjectType.DIGITALIZATION_PROJECT) {
        getDigitalizationProjectsCoworkers();
      }
    } catch {
      createErrorToast(t('addCoworkerToProject.errors.couldNotAddCoworker'));
    }
    isAddingCoworker.value = false;
  }

  function nameAndEmailLabel({
    firstName,
    lastName,
    email,
  }: {
    firstName: string;
    lastName: string;
    email: string;
  }) {
    if (firstName === null && lastName === null) {
      firstName = t('ADModal.noData');
      lastName = '';
    } else if (firstName === null) {
      firstName = t('ADModal.noData');
    } else if (lastName === null) {
      lastName = t('ADModal.noData');
    }
    if (email) {
      return `${firstName} ${lastName} — [${email}]`;
    } else {
      return `${firstName} ${lastName}`;
    }
  }

  const modalRef = ref<InstanceType<typeof SelectModal> | null>(null);
  function openAddCoworkerModal() {
    modalRef.value?.show();
  }

  onMounted(() => {
    if (!props.projectId) {
      return;
    }
    if (props.mode === ProjectType.SUPPLIER_PROJECT) {
      getProjectsCoworkers();
    } else if (props.mode === ProjectType.DIGITALIZATION_PROJECT) {
      getDigitalizationProjectsCoworkers();
    }
  });
</script>

<style scoped lang="scss">
  #title-panel {
    margin-top: 37px;
    margin-bottom: 3.125rem;
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 2fr 1fr;
    align-items: center;
    justify-items: center;

    #title-container {
      justify-self: flex-start;
      display: flex;
      align-items: flex-start;
      column-gap: 0.625rem;

      h2 {
        @include subheading;
      }

      #coworkers-counter {
        background-color: black;
        color: white;
        font-size: 12px;
        border-radius: 50px;
        padding: 3px 20px;
      }
    }

    p#description {
      align-self: start;
      width: 645px;
      font-size: 16px;
      line-height: 28px;
      font-weight: 500;
    }

    button#add-coworker-button {
      justify-self: end;
    }
  }
</style>
